import { withStyleNonce } from '@/lib/csp-nonce';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { LinkReportRow } from '../types/reports.types';
import { fmtNum } from '../utils/report-format';

/** dataviz sequential blue: steps 200 / 350 / 450 / 600. Zero = neutral. */
export const HEATMAP_SCALE = [
  { bg: '#EEF0F1', fg: '#1B2021', ring: 'rgba(27,32,33,.18)' },
  { bg: '#9ec5f4', fg: '#0b0b0b', ring: 'rgba(11,11,11,.20)' },
  { bg: '#5598e7', fg: '#0b0b0b', ring: 'rgba(11,11,11,.22)' },
  { bg: '#2a78d6', fg: '#ffffff', ring: 'rgba(255,255,255,.55)' },
  { bg: '#184f95', fg: '#ffffff', ring: 'rgba(255,255,255,.55)' },
] as const;

export function heatmapBucketFor(clicks: number, max: number): number {
  if (!clicks) return 0;
  if (!max) return 1;
  const f = clicks / max;
  if (f <= 0.25) return 1;
  if (f <= 0.5) return 2;
  if (f <= 0.75) return 3;
  return 4;
}

function bucketRanges(max: number): Array<{ i: number; lo: number; hi: number }> {
  if (!max) return [];
  return [
    { i: 1, lo: 1, hi: Math.max(1, Math.floor(max * 0.25)) },
    { i: 2, lo: Math.floor(max * 0.25) + 1, hi: Math.floor(max * 0.5) },
    { i: 3, lo: Math.floor(max * 0.5) + 1, hi: Math.floor(max * 0.75) },
    { i: 4, lo: Math.floor(max * 0.75) + 1, hi: max },
  ].filter((b) => b.hi >= b.lo);
}

type Rect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

/**
 * Beefree buttons often paint the fill on a parent cell and stamp `data-link-id`
 * on the inner text link. Measuring only that text puts the count inside the button.
 * Use the painted parent when it is a snug wrapper around this one link.
 */
export function heatmapAnchor(el: Element): Element {
  const anchor = el.closest('a') ?? el;
  const parent = anchor.parentElement;
  if (!parent) return anchor;
  const tag = parent.tagName;
  if (tag !== 'TD' && tag !== 'TH' && tag !== 'DIV' && tag !== 'SPAN') return anchor;
  if (parent.querySelectorAll('a').length !== 1) return anchor;
  const style = `${parent.getAttribute('style') || ''} ${parent.getAttribute('bgcolor') || ''}`;
  const painted = /background|bgcolor/i.test(style) || Boolean(parent.getAttribute('bgcolor'));
  if (!painted && tag !== 'TD' && tag !== 'TH') return anchor;
  const parentRect = parent.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const slackW = parentRect.width - anchorRect.width;
  const slackH = parentRect.height - anchorRect.height;
  if (slackW >= 0 && slackW < 96 && slackH >= 0 && slackH < 48) return parent;
  return anchor;
}

/** Iframe `getBoundingClientRect` is local in some browsers and page-viewport in others. */
export function rectInFrame(el: Element, frame: HTMLIFrameElement): Rect {
  const rect = el.getBoundingClientRect();
  const body = frame.contentDocument?.body.getBoundingClientRect();
  const frameBox = frame.getBoundingClientRect();
  const parentViewport =
    body != null && Math.abs(body.top - frameBox.top) < 4 && Math.abs(frameBox.top) > 8;
  const left = parentViewport ? rect.left - frameBox.left : rect.left;
  const top = parentViewport ? rect.top - frameBox.top : rect.top;
  return {
    left,
    top,
    right: left + rect.width,
    bottom: top + rect.height,
    width: rect.width,
    height: rect.height,
  };
}

type LinkHeatmapOverlayProps = {
  html: string;
  rows: LinkReportRow[];
  maxClicks: number;
  hotLinkId: string | null;
  onHover: (linkId: string | null) => void;
  className?: string;
};

/**
 * Renders sent email HTML in a sandboxed iframe and lays click-count badges
 * over each [data-link-id] anchor (prototype heatmap-core.js).
 */
export function LinkHeatmapOverlay({
  html,
  rows,
  maxClicks,
  hotLinkId,
  onHover,
  className,
}: LinkHeatmapOverlayProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const [unplaced, setUnplaced] = useState<LinkReportRow[]>([]);
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const srcdoc =
      '<!doctype html><html><head><meta charset="utf-8">' +
      '<style>html,body{margin:0;padding:0}' +
      'img{max-width:100%}a{cursor:default}</style>' +
      '</head><body>' +
      html +
      '</body></html>';

    const draw = () => {
      const doc = frame.contentDocument;
      const layer = layerRef.current;
      if (!doc?.body || !layer) return;

      frame.style.height = `${Math.max(240, doc.body.scrollHeight)}px`;
      layer.innerHTML = '';

      const placeable: Array<{ row: LinkReportRow; rect: Rect }> = [];
      const missing: LinkReportRow[] = [];

      rows.forEach((r) => {
        if (!r.linkId) return;
        const a = doc.querySelector(`[data-link-id="${CSS.escape(r.linkId)}"]`);
        if (!a) {
          missing.push(r);
          return;
        }
        placeable.push({ row: r, rect: rectInFrame(heatmapAnchor(a), frame) });
      });
      setUnplaced(missing);

      placeable.sort((x, y) => y.row.clicks - x.row.clicks);
      const anchorRects = placeable.map((a) => a.rect);
      const placed: Rect[] = [];

      const hits = (box: Rect, list: Rect[], pad = 0) => {
        for (const o of list) {
          if (
            box.left < o.right + pad &&
            box.right > o.left - pad &&
            box.top < o.bottom + pad &&
            box.bottom > o.top - pad
          ) {
            return true;
          }
        }
        return false;
      };

      const slotFor = (rect: Rect, w: number, h: number) => {
        const others = anchorRects.filter((o) => o !== rect);
        const cands = [
          { left: rect.right + 8, top: rect.top + rect.height / 2 - h / 2 },
          { left: rect.right + 8, top: rect.top - h - 4 },
          { left: rect.left, top: rect.top - h - 4 },
          { left: rect.right + 8, top: rect.bottom + 4 },
          { left: rect.left, top: rect.bottom + 4 },
          { left: rect.left - w - 8, top: rect.top + rect.height / 2 - h / 2 },
        ];
        for (const c of cands) {
          const box = {
            left: c.left,
            top: c.top,
            right: c.left + w,
            bottom: c.top + h,
            width: w,
            height: h,
          };
          if (box.left < 2) continue;
          if (hits(box, placed, 3)) continue;
          if (hits(box, others, 2)) continue;
          return c;
        }
        let t2 = rect.bottom + 4;
        for (let k = 0; k < 12; k++) {
          const b2 = {
            left: rect.left,
            top: t2,
            right: rect.left + w,
            bottom: t2 + h,
            width: w,
            height: h,
          };
          if (!hits(b2, placed, 3)) return { left: rect.left, top: t2 };
          t2 += h + 3;
        }
        return { left: rect.left, top: rect.bottom + 4 };
      };

      placeable.forEach(({ row: r, rect }) => {
        const b = heatmapBucketFor(r.clicks, maxClicks);
        const c = HEATMAP_SCALE[b];

        const ring = document.createElement('div');
        ring.style.cssText =
          `position:absolute;pointer-events:none;` +
          `left:${rect.left - 3}px;top:${rect.top - 2}px;` +
          `width:${rect.width + 6}px;height:${rect.height + 4}px;` +
          `border:1.5px solid ${c.bg};border-radius:6px;` +
          `box-shadow:0 0 0 3px ${b ? 'rgba(42,120,214,.10)' : 'rgba(27,32,33,.05)'};`;
        layer.appendChild(ring);

        const text = fmtNum(r.clicks);
        const bw = Math.max(26, 14 + text.length * 7);
        const bh = 22;
        const slot = slotFor(rect, bw, bh);
        placed.push({
          left: slot.left,
          top: slot.top,
          right: slot.left + bw,
          bottom: slot.top + bh,
          width: bw,
          height: bh,
        });

        const badge = document.createElement('button');
        badge.type = 'button';
        badge.setAttribute('data-hm-badge', r.linkId);
        badge.title = `${r.label} — ${fmtNum(r.clicks)} clicks, ${fmtNum(r.uniqueClicks)} unique`;
        badge.setAttribute(
          'aria-label',
          `${r.label}: ${fmtNum(r.clicks)} clicks, ${fmtNum(r.uniqueClicks)} unique recipients`
        );
        badge.textContent = text;
        badge.style.cssText =
          `position:absolute;pointer-events:auto;cursor:pointer;z-index:2;box-sizing:border-box;` +
          `left:${slot.left}px;top:${slot.top}px;` +
          `min-width:${bw}px;width:${bw}px;height:${bh}px;padding:0 6px;border:0;border-radius:999px;` +
          `background:${c.bg};color:${c.fg};font:600 11px/22px Inter,system-ui,sans-serif;` +
          `font-variant-numeric:tabular-nums;text-align:center;transition:transform .12s ease;` +
          `box-shadow:0 0 0 2px #fff,0 1px 3px rgba(16,24,40,.18);`;
        badge.addEventListener('mouseenter', () => onHoverRef.current(r.linkId));
        badge.addEventListener('mouseleave', () => onHoverRef.current(null));
        badge.addEventListener('focus', () => onHoverRef.current(r.linkId));
        badge.addEventListener('blur', () => onHoverRef.current(null));
        layer.appendChild(badge);
      });
    };

    const drawWhenReady = () => {
      draw();
      const imgs = frame.contentDocument?.querySelectorAll('img') ?? [];
      imgs.forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', draw, { once: true });
        img.addEventListener('error', draw, { once: true });
      });
    };

    frame.addEventListener('load', drawWhenReady);
    frame.srcdoc = withStyleNonce(srcdoc);
    /* srcdoc can fire load sync in some browsers — redraw once after attach. */
    if (frame.contentDocument?.body) drawWhenReady();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => draw()) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    else window.addEventListener('resize', draw);

    return () => {
      frame.removeEventListener('load', drawWhenReady);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', draw);
    };
  }, [html, rows, maxClicks]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.querySelectorAll<HTMLElement>('[data-hm-badge]').forEach((el) => {
      const on = hotLinkId && el.getAttribute('data-hm-badge') === hotLinkId;
      el.style.transform = on ? 'scale(1.25)' : 'scale(1)';
      el.style.zIndex = on ? '5' : '2';
    });
  }, [hotLinkId]);

  const ranges = bucketRanges(maxClicks);

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        ref={wrapRef}
        className="relative overflow-hidden rounded-xl border border-[#E7E8EA] bg-[#F4F5F6]"
      >
        <iframe
          ref={frameRef}
          title="Sent email preview"
          sandbox="allow-same-origin"
          scrolling="no"
          className="block w-full border-0 bg-[#F4F5F6]"
          style={{ pointerEvents: 'none' }}
        />
        <div ref={layerRef} className="pointer-events-none absolute inset-0" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <span className="text-xs font-medium text-neutral-700">Clicks</span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block size-3 rounded-sm border"
            style={{ background: HEATMAP_SCALE[0].bg, borderColor: HEATMAP_SCALE[0].ring }}
          />
          <span className="text-xs text-neutral-600">0 — never clicked</span>
        </span>
        {ranges.map((b) => (
          <span key={b.i} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block size-3 rounded-sm"
              style={{ background: HEATMAP_SCALE[b.i].bg }}
            />
            <span className="text-xs text-neutral-600">
              {b.lo === b.hi ? fmtNum(b.lo) : `${fmtNum(b.lo)}–${fmtNum(b.hi)}`}
            </span>
          </span>
        ))}
      </div>
      <p className="mt-2 text-xs text-neutral-500">
        Scale is relative to this send’s most-clicked link ({fmtNum(maxClicks)}). Badges show
        clicks; hover one to highlight its row. Remote images are not loaded in this preview.
      </p>

      {unplaced.length > 0 ? (
        <div className="mt-3 rounded-lg border border-[#F0C98A] bg-[#FFFCF5] p-3.5">
          <p className="text-xs font-medium text-neutral-900">
            {unplaced.length} link{unplaced.length === 1 ? '' : 's'} could not be placed on the
            preview
          </p>
          <p className="mt-1 text-xs text-neutral-600">
            Their clicks are counted here rather than guessed onto the wrong element.
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {unplaced.map((r) => (
              <li
                key={r.linkId}
                className="flex items-center justify-between gap-3 text-xs text-neutral-700"
              >
                <span className="truncate">{r.label || r.url}</span>
                <span className="shrink-0 font-mono">{fmtNum(r.clicks)} clicks</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
